import React, { useState, useEffect } from 'react';
import { DoorElement, Room } from '../types';
import { createPortal } from 'react-dom';
import { Camera, X, Image, DoorOpen, RotateCcw, Trash2, Home, ChevronLeft, ChevronRight } from 'lucide-react';
import PhotoCapture from './PhotoCapture';
import PhotoGalleryModal from './PhotoGalleryModal';
import { PhotoData } from '../services/CameraService';
import CameraService from '../services/CameraService';

interface DoorPhoto {
  id: string;
  fileName: string;
  localPath: string;
  url: string;
  timestamp: string;
  order: number;
}

interface DoorUndercutPromptProps {
  isOpen: boolean;
  doorElement: DoorElement | null;
  rooms: Room[];
  onSave: (hasUndercut: boolean, photos?: DoorPhoto[], roomId?: string) => void;
  onFlip?: () => void;
  onDelete: () => void;
  onClose: () => void;
  isFullscreen?: boolean;
  skipWallImages?: boolean;
}

const DoorUndercutPrompt: React.FC<DoorUndercutPromptProps> = ({ 
  isOpen, 
  doorElement, 
  rooms,
  onSave, 
  onFlip,
  onDelete,
  onClose,
  isFullscreen = false,
  skipWallImages = false
}) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [undercutSize, setUndercutSize] = useState<'More Than 10mm' | 'Less than 10mm' | ''>('More Than 10mm');
  const [photos, setPhotos] = useState<DoorPhoto[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<string>('');
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  useEffect(() => {
    if (doorElement) {
      setStep(1);
      // Map hasUndercut boolean to size string
      if (doorElement.hasUndercut === true) {
        setUndercutSize('More Than 10mm');
      } else if (doorElement.hasUndercut === false) {
        setUndercutSize('Less than 10mm');
      } else {
        setUndercutSize('More Than 10mm'); // Default
      }
      // Initialize selected room from door element
      setSelectedRoomId(doorElement.roomId || '');
      setPhotos([]);
    }
  }, [doorElement]);

  // Handle photo capture
  const handlePhotosCaptured = (newPhotos: PhotoData[]) => {
    console.log(`[DoorModal] Photos captured: ${newPhotos.length}`);
    
    // Convert PhotoData to DoorPhoto format
    const doorPhotos: DoorPhoto[] = newPhotos.map(photo => ({
      id: `door-photo-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      fileName: `door-photo-${Date.now()}-${Math.random().toString(36).substring(2, 9)}.jpg`,
      localPath: `data:image/${photo.format};base64,${photo.base64String}`,
      url: `data:image/${photo.format};base64,${photo.base64String}`,
      timestamp: new Date().toISOString(),
      order: photos.length
    }));
    
    // Add to existing photos
    setPhotos(prev => [...prev, ...doorPhotos]);
  };

  // Remove photo
  const removePhoto = (photoId: string) => {
    setPhotos(prev => prev.filter(photo => photo.id !== photoId));
  };

  // Handle next step
  const handleNextStep = () => {
    if (skipWallImages) {
      // Skip photo step for FloorPlanCreator - save directly
      handleSave();
    } else {
      setStep(2);
    }
  };

  // Handle delete photo (for gallery)
  const handleDeletePhoto = (photoId: string) => {
    setPhotos(prev => prev.filter(photo => photo.id !== photoId));
  };

  // Handle update note (for gallery)
  const handleUpdateNote = (photoId: string, note: string) => {
    setPhotos(prev => prev.map(photo =>
      photo.id === photoId ? { ...photo, note } as DoorPhoto : photo
    ));
  };

  // Render camera button
  const renderCameraButton = () => {
    const cameraService = CameraService.getInstance();
    const supportsCustomCamera = cameraService.supportsCustomCamera();
    
    if (supportsCustomCamera) {
      return (
        <PhotoCapture
          onPhotosCaptured={handlePhotosCaptured}
          maxPhotos={10}
          buttonLabel="Take Photos"
          buttonClass="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-lg shadow-blue-500/25 transition-all duration-200"
        />
      );
    } else {
      return (
        <button
          type="button"
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-lg shadow-blue-500/25 transition-all duration-200"
          onClick={() => console.warn('Camera not available on this device')}
        >
          <Camera className="w-4 h-4" />
          <span>Take Photos</span>
        </button>
      );
    }
  };

  if (!isOpen || !doorElement) {
    return null;
  }

  const handleSave = () => {
    // Convert size string to boolean for backward compatibility
    const hasUndercut = undercutSize === 'More Than 10mm';
    onSave(hasUndercut, photos, selectedRoomId || undefined);
    onClose();
  };

  const handleSkip = () => {
    // Convert size string to boolean for backward compatibility
    const hasUndercut = undercutSize === 'More Than 10mm';
    onSave(hasUndercut, [], selectedRoomId || undefined);
    onClose();
  };

  const handleDelete = () => {
    onDelete();
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className={`fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/60 to-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 ${isFullscreen ? 'z-[10003]' : 'z-[999998]'}`}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-hidden border border-slate-200/50">
        {/* Modern Header with Gradient */}
        <div className="relative bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2.5">
              <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-sm">
                {step === 1 ? <DoorOpen className="w-5 h-5 text-white" /> : <Camera className="w-5 h-5 text-white" />}
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">
                  {step === 1 ? 'Door Configuration' : 'Door Photos'}
                </h3>
                <p className="text-blue-100 text-xs">
                  {step === 1 ? 'Configure swing direction & undercut size' : 'Capture door undercut images'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="bg-white/20 text-white px-2.5 py-1 rounded-lg text-xs font-semibold backdrop-blur-sm">
                Floor {doorElement?.floor}
              </span>
            </div>
          </div>
          {/* Step Indicator */}
          <div className="flex items-center space-x-2 mt-3">
            <div className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
              step >= 1 ? 'bg-white' : 'bg-white/30'
            }`}></div>
            <div className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
              step >= 2 ? 'bg-white' : 'bg-white/30'
            }`}></div>
          </div>
          {/* Decorative gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/0 via-white/5 to-indigo-600/0 pointer-events-none"></div>
        </div>

        {/* Content Area */}
        <div className="px-5 py-5 space-y-5 max-h-[calc(85vh-180px)] overflow-y-auto custom-scrollbar">
          {/* Step 1: Door Configuration */}
          {step === 1 && (
            <>
              {/* Room Assignment Section */}
              <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
            <h4 className="text-xs font-bold text-slate-800 mb-3 flex items-center">
              <Home className="w-3.5 h-3.5 mr-1.5 text-slate-600" />
              Assign to Room
            </h4>
            <div className="space-y-2">
              <p className="text-xs text-slate-600 mb-2">Select which room this door belongs to</p>
              <select
                value={selectedRoomId}
                onChange={(e) => setSelectedRoomId(e.target.value)}
                className="w-full px-3 py-2.5 text-sm bg-white border-2 border-slate-200 rounded-lg hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
              >
                <option value="">-- Select Room --</option>
                {rooms
                  .filter(room => room.walls.length > 0 && room.walls[0].floor === doorElement?.floor)
                  .map(room => (
                    <option key={room.stableId || room.id} value={room.stableId || room.id}>
                      {room.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Door Swing Direction Section */}
          <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
            <h4 className="text-xs font-bold text-slate-800 mb-3 flex items-center">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5 text-slate-600" />
              Door Swing Direction
            </h4>
            <div className="text-center space-y-3">
              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-xs font-medium text-slate-600 mb-1">Current Direction</p>
                <p className="text-sm font-semibold text-slate-800">
                  {doorElement?.swingRight ? 'Swings Right →' : '← Swings Left'}
                </p>
              </div>
              <button
                onClick={onFlip}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-lg shadow-blue-500/25 transition-all duration-200"
              >
                <RotateCcw className="w-4 h-4" />
                Flip Door Swing
              </button>
            </div>
          </div>

          {/* Undercut Size Selection Section */}
          <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
            <h4 className="text-xs font-bold text-slate-800 mb-3 flex items-center">
              <svg className="w-3.5 h-3.5 mr-1.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
              Door Undercut Size
            </h4>
            <div className="space-y-2">
              <p className="text-xs text-slate-600 mb-3">Select the size of the undercut gap at the bottom of the door</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setUndercutSize('More Than 10mm')}
                  className={`px-3 py-2.5 text-xs font-semibold text-center rounded-lg transition-all duration-200 ${
                    undercutSize === 'More Than 10mm' 
                      ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/25' 
                      : 'bg-white text-slate-700 border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  More Than 10mm
                </button>
                <button
                  onClick={() => setUndercutSize('Less than 10mm')}
                  className={`px-3 py-2.5 text-xs font-semibold text-center rounded-lg transition-all duration-200 ${
                    undercutSize === 'Less than 10mm' 
                      ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/25' 
                      : 'bg-white text-slate-700 border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  Less than 10mm
                </button>
              </div>
            </div>
          </div>
            </>
          )}

          {/* Step 2: Photo Capture */}
          {step === 2 && (
            <>
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  Capture Door Undercut Images {!skipWallImages && <span className="text-xs text-gray-500 ml-2">(Optional)</span>}
                </h4>
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <p className="text-sm text-blue-800">
                    📸 Take photos of the door undercut (gap at bottom of door). This helps document the door's airflow characteristics.
                  </p>
                </div>
              </div>

              {/* Photo Capture Component */}
              <PhotoCapture
                onPhotosCaptured={handlePhotosCaptured}
                buttonLabel="Take Photos"
                maxPhotos={10}
              />

              {/* Captured Images Preview */}
              {photos.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    Captured Images ({photos.length})
                  </h4>
                  <button
                    onClick={() => setIsGalleryOpen(true)}
                    className="relative w-full aspect-video rounded-xl overflow-hidden border-2 border-blue-200 hover:border-blue-400 transition-all group"
                  >
                    <img
                      src={photos[0].url}
                      alt="Door undercut"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-all flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="text-2xl font-bold">{photos.length}</div>
                        <div className="text-sm">Click to view all</div>
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modern Action Bar */}
        <div className="px-5 py-4 bg-slate-50 border-t border-slate-200">
          {step === 1 ? (
            <div className="flex justify-between items-center">
              <button
                onClick={handleDelete}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-gradient-to-r from-red-600 to-red-500 rounded-lg hover:from-red-700 hover:to-red-600 focus:outline-none focus:ring-2 focus:ring-red-500/20 shadow-lg shadow-red-500/25 transition-all duration-200"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete
              </button>
              <div className="flex space-x-2">
                <button
                  onClick={onClose}
                  className="px-3 py-2 text-xs font-semibold text-slate-700 bg-white border-2 border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500/20 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleNextStep}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-lg shadow-blue-500/25 transition-all duration-200"
                >
                  Next
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-between items-center">
              <button
                onClick={() => setStep(1)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 bg-white border-2 border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500/20 transition-all duration-200"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Back
              </button>
              <div className="flex space-x-2">
                <button
                  onClick={handleSkip}
                  className="px-3 py-2 text-xs font-semibold text-slate-700 bg-white border-2 border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500/20 transition-all duration-200"
                >
                  Skip
                </button>
                <button
                  onClick={handleSave}
                  className={`px-4 py-2 text-xs font-semibold rounded-lg focus:outline-none focus:ring-2 shadow-lg transition-all duration-200 ${
                    photos.length > 0
                      ? 'text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:ring-green-500/20 shadow-green-500/25'
                      : 'text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 focus:ring-blue-500/20 shadow-blue-500/25'
                  }`}
                >
                  Save {photos.length > 0 && `(${photos.length})`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Photo Gallery Modal */}
      {isGalleryOpen && (
        <PhotoGalleryModal
          isOpen={isGalleryOpen}
          onClose={() => setIsGalleryOpen(false)}
          photos={photos.map(photo => ({
            id: photo.id,
            url: photo.url,
            localPath: photo.localPath,
            fileName: photo.fileName
          }))}
          sectionTitle="Door Undercut Images"
          onDeletePhoto={handleDeletePhoto}
          onUpdateNote={handleUpdateNote}
          zIndex={10000001}
        />
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default DoorUndercutPrompt; 